import { createApi } from "@reduxjs/toolkit/query/react"
import { baseQueryWithReauth } from "./setup/baseQuery"
import { combineDuplicatedRecognitionsWithCount } from "@/utils/dedupeRecognitions"

// New api endpoint by Dimash
const ADMIN_SERVICE_BASE_URL = import.meta.env.DEV
  ? '/admin-service-proxy' 
  : import.meta.env.VITE_ADMIN_SERVICE_API

export const adminServiceApiSlice = createApi({
  reducerPath: 'adminServiceApi',
  baseQuery: baseQueryWithReauth({baseUrl: ADMIN_SERVICE_BASE_URL}),
  endpoints: () => ({}),
  tagTypes: ['LastEntries', 'OpenReasonSetting', 'VehicleTypeOverrides'],
})

const lastEntriesApi = adminServiceApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    
    getLastEntries: builder.query({
      query: ({ 
        buildingId, 
        channelIds, 
        page = 1, 
        size = 10,
        search = '' 
      }) => ({
        url: `api/v2/last_entries`,
        method: 'GET',
        params: { 
          building_id: buildingId,
          ...(channelIds?.length ? { channel_ids: channelIds } : {}),
          ...(search ? { search } : {}),
          page, 
          size, 
        }
      }),
      providesTags: ['LastEntries'],
      keepUnusedDataFor: 0,
    }),

    getLastEntriesPages: builder.infiniteQuery({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam, _allPageParams, _queryArg) => {
          return lastPage?.metadata?.page_count > lastPageParam ? lastPageParam + 1 : undefined
        },
        getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
          return firstPageParam > 0 ? firstPageParam - 1 : undefined
        },

      },
      query: (args) => {
        const queryArg = args?.queryArg ?? {}
        const pageParam = args?.pageParam ?? 1
        const {
          buildingId,
          channelIds,
          size = 20,
          from,
          to,
          search = '',
          // live-лента: бэк ограничивает page_count окном последних записей
          feed = false
        } = queryArg

        return {
        url: 'api/v2/last_entries',
        method: 'GET',
        params: {
          building_id: buildingId,
          ...(channelIds?.length ? { channel_ids: channelIds } : {}),
          ...(search ? { search } : {}),
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          ...(feed ? { feed: 1 } : {}),
          page: pageParam,
          size
        }
        }
      },
      transformResponse: (response) => ({
        ...response,
        data: combineDuplicatedRecognitionsWithCount(response.data)
      }),
      providesTags: ['LastEntries'],
      keepUnusedDataFor: 0
    }),

    getEntriesHistory: builder.query({
      query: ({ buildingId, from, to, statuses, payments, search, page = 1, size = 50 }) => ({
        url: `api/v2/buildings/${buildingId}/entries_history`,
        method: 'GET',
        // `status` and `payment` are repeatable query params,
        // so we pass an array of [key, value] pairs instead of an object
        params: [
          ...(from ? [['from', from]] : []),
          ...(to ? [['to', to]] : []),
          ...(search ? [['search', search]] : []),
          ['page', page],
          ['size', size],
          ...(statuses?.length ? statuses.map((s) => ['status', s]) : []),
          ...(payments?.length ? payments.map((p) => ['payment', p]) : [])
        ]
      }),
      keepUnusedDataFor: 0
    }),

    getSessionsByPlateNumber: builder.query({
      query: (arg) => {
        const { buildingId, plateNumber } = arg ?? {}
        if (!buildingId || !plateNumber) throw new Error('Building ID and plate number are required')
        return {
          url: `api/v2/buildings/${buildingId}/sessions`,
          method: 'GET',
          params: { plate_number: plateNumber },
        }
      },
      keepUnusedDataFor: 0,
    }),
    editSessionDebt: builder.mutation({
      query: ({ buildingId, plateNumber, billingId, debtAmount, debtFixed = true, reason }) => ({
        url: `api/v2/manual/session/debt`,
        method: 'POST',
        body: { 
          building_id: buildingId,
          ...(billingId != null ? { billing_id: billingId } : {}),
          debt_amount: debtAmount,
          plate_number: plateNumber,
          debt_fixed: debtFixed,
          reason,
        },
      }),
      async onQueryStarted({ buildingId, plateNumber, billingId }, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled
          const payload = responseData.data ?? responseData
          const changedBillingId = payload.billing_id ?? billingId
          if (!changedBillingId || !plateNumber) return

          dispatch(lastEntriesApi.util.updateQueryData('getSessionsByPlateNumber', {
            buildingId,
            plateNumber,
          }, (draft) => {
            const idx = draft.findIndex((item) => item.billing_id === changedBillingId)
            if (idx < 0) return
            draft[idx] = {
              ...draft[idx],
              amount: payload.applied_total,
              paid_amount: payload.paid_amount,
              debt_amount: payload.applied_debt,
              debt_fixed: payload.debt_fixed,
            }
          }))
        } catch (error) {
          console.error(error)
        }
      },
    }),
    editSessionVehicleType: builder.mutation({
      query: ({ buildingId, billingId, vehicleType, reason }) => ({
        url: `api/v2/manual/session/vehicle_type`,
        method: 'PATCH',
        body: {
          building_id: buildingId,
          billing_id: billingId,
          vehicle_type: vehicleType,
          reason,
        },
      }),
      async onQueryStarted({ buildingId, plateNumber }, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled
          const changedBillingId = responseData?.billing_id
          if (!changedBillingId || !plateNumber) return
          dispatch(lastEntriesApi.util.updateQueryData('getSessionsByPlateNumber', {
            buildingId,
            plateNumber,
          }, (draft) => {
            const idx = draft.findIndex((item) => item.billing_id === changedBillingId)
            if (idx < 0) return
            draft[idx] = {
              ...draft[idx],
              vehicle_type: responseData.new_vehicle_type,
              amount: responseData.new_amount,
              paid_amount: responseData.paid_amount,
              debt_amount: responseData.debt_amount,
            }
          }))
        } catch (error) {
          console.log('error', error)
        }
      },
    }),
    getBuildingVehicleTypes: builder.query({
      query: (buildingId) => ({
        url: `api/v2/buildings/${buildingId}/vehicle_types`,
        method: 'GET',
      }),
    }),
    getChannelStream: builder.query({
      query: ({ channelId, quality = 'sd' }) => ({
        url: `api/v2/channels/${channelId}/stream`,
        method: 'GET',
        params: { quality },
      }),
      keepUnusedDataFor: 60,
    })
  })
})

export const {
  useGetLastEntriesQuery,
  useGetLastEntriesPagesInfiniteQuery,
  useLazyGetSessionsByPlateNumberQuery,
  useGetSessionsByPlateNumberQuery,
  useEditSessionDebtMutation,
  useEditSessionVehicleTypeMutation,
  useGetBuildingVehicleTypesQuery,
  useGetChannelStreamQuery,
  useGetEntriesHistoryQuery,
} = lastEntriesApi;

const REPORT_QUERY_RETRY = { maxRetries: 5 }

const reportsApi = adminServiceApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getReportRevenue: builder.query({
      query: ({ buildingId, from, to, groupBy = 'day' }) => ({
        url: `api/v2/buildings/${buildingId}/reports/revenue`,
        method: 'GET',
        params: {
          from,
          to,
          group_by: groupBy,
        },
      }),
      extraOptions: REPORT_QUERY_RETRY,
      keepUnusedDataFor: 300,
    }),
    getReportOccupancy: builder.query({
      query: ({ buildingId, from, to, interval = 'hour' }) => ({
        url: `api/v2/buildings/${buildingId}/reports/occupancy`,
        method: 'GET',
        params: {
          from,
          to,
          interval,
        },
      }),
      extraOptions: REPORT_QUERY_RETRY,
      keepUnusedDataFor: 300,
    }),
    getReportEntries: builder.query({
      query: ({ buildingId, from, to, groupBy = 'day' }) => ({
        url: `api/v2/buildings/${buildingId}/reports/entries`,
        method: 'GET',
        params: {
          from,
          to,
          group_by: groupBy,
        },
      }),
      extraOptions: REPORT_QUERY_RETRY,
      keepUnusedDataFor: 300,
    }),
    getReportTariffs: builder.query({
      query: ({ buildingId, from, to }) => ({
        url: `api/v2/buildings/${buildingId}/reports/tariffs`,
        method: 'GET',
        params: {
          from,
          to,
        },
      }),
      extraOptions: REPORT_QUERY_RETRY,
      keepUnusedDataFor: 300,
    }),
  }),
});

export const {
  useGetReportRevenueQuery,
  useGetReportOccupancyQuery,
  useGetReportEntriesQuery,
  useGetReportTariffsQuery,
} = reportsApi;

export const {
  useInfiniteQueryState: useGetLastEntriesPagesInfiniteQueryState,
} = lastEntriesApi.endpoints.getLastEntriesPages;

const gateOpenReasonApi = adminServiceApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getOpenReasonSetting: builder.query({
      query: (buildingId) => ({
        url: `api/v2/buildings/${buildingId}/open_reason_setting`,
        method: 'GET',
      }),
      providesTags: ['OpenReasonSetting'],
    }),
    updateOpenReasonSetting: builder.mutation({
      query: ({ buildingId, requireOpenReason }) => ({
        url: `api/v2/buildings/${buildingId}/open_reason_setting`,
        method: 'PUT',
        body: { require_open_reason: requireOpenReason },
      }),
      invalidatesTags: ['OpenReasonSetting'],
    }),
  }),
})

export const {
  useGetOpenReasonSettingQuery,
  useUpdateOpenReasonSettingMutation,
} = gateOpenReasonApi;

const vehicleTypeOverridesApi = adminServiceApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getVehicleTypeOverrides: builder.query({
      query: ({ buildingId, search = '', page = 1, size = 10 }) => ({
        url: `api/v2/buildings/${buildingId}/vehicle_type_overrides`,
        method: 'GET',
        params: {
          page,
          size,
          ...(search ? { search } : {}),
        },
      }),
      providesTags: ['VehicleTypeOverrides'],
    }),
    createVehicleTypeOverride: builder.mutation({
      query: ({ buildingId, plateNumber, vehicleType, comment }) => ({
        url: `api/v2/buildings/${buildingId}/vehicle_type_overrides`,
        method: 'POST',
        body: { plate_number: plateNumber, vehicle_type: vehicleType, comment },
      }),
      invalidatesTags: ['VehicleTypeOverrides'],
    }),
    updateVehicleTypeOverride: builder.mutation({
      query: ({ buildingId, id, vehicleType, comment }) => ({
        url: `api/v2/buildings/${buildingId}/vehicle_type_overrides/${id}`,
        method: 'PATCH',
        body: { vehicle_type: vehicleType, comment },
      }),
      invalidatesTags: ['VehicleTypeOverrides'],
    }),
    deleteVehicleTypeOverride: builder.mutation({
      query: ({ buildingId, id }) => ({
        url: `api/v2/buildings/${buildingId}/vehicle_type_overrides/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['VehicleTypeOverrides'],
    }),
  }),
})

export const {
  useGetVehicleTypeOverridesQuery,
  useCreateVehicleTypeOverrideMutation,
  useUpdateVehicleTypeOverrideMutation,
  useDeleteVehicleTypeOverrideMutation,
} = vehicleTypeOverridesApi;

const bulkDebtApi = adminServiceApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getBulkDebtPreview: builder.query({
      query: ({ buildingId, from, to, plateNumber, openMode }) => ({
        url: `api/v2/buildings/${buildingId}/debt_write_off/preview`,
        method: 'GET',
        params: {
          from,
          to,
          open_mode: openMode,
          ...(plateNumber ? { plate_number: plateNumber } : {}),
        },
      }),
      keepUnusedDataFor: 0,
    }),
    bulkDebtWriteOff: builder.mutation({
      query: ({ buildingId, from, to, plateNumber, openMode, reason }) => ({
        url: `api/v2/buildings/${buildingId}/debt_write_off`,
        method: 'POST',
        body: {
          from,
          to,
          open_mode: openMode,
          reason,
          ...(plateNumber ? { plate_number: plateNumber } : {}),
        },
      }),
      invalidatesTags: ['LastEntries'],
    }),
  }),
})

export const {
  useLazyGetBulkDebtPreviewQuery,
  useBulkDebtWriteOffMutation,
} = bulkDebtApi;
